/**
 * Shared Telegram bot-token configuration (onboarding + Settings).
 * Configure endpoint validates the token and persists in one step.
 */
import PropTypes from "prop-types";
import { useCallback, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { channelsApi } from "@/lib/api";
import { Loader2, ShieldCheck } from "lucide-react";
import { telegramConfigurePropType } from "@/lib/propTypes/connections";

const TELEGRAM_TOKEN_RE = /^\d{5,12}:[A-Za-z0-9_-]{20,}/;

function normalizeToken(raw) {
  return (raw || "").trim().replace(/\uFEFF/g, "").replace(/\u200b/g, "");
}

function isHttpsWebhook(url) {
  return /^https:\/\/.+/i.test((url || "").trim());
}

export function useTelegramConfigure({ connectionId, ensureConnection, canEdit, onConfigured }) {
  const [token, setToken] = useState("");
  const [botName, setBotName] = useState("");
  const [botUsername, setBotUsername] = useState("");
  const [webhookUrl, setWebhookUrl] = useState("");
  const [validated, setValidated] = useState(false);
  const [testBusy, setTestBusy] = useState(false);
  const [connectBusy, setConnectBusy] = useState(false);
  const [error, setError] = useState(null);

  const tokenValid = TELEGRAM_TOKEN_RE.test(normalizeToken(token));
  const busy = testBusy || connectBusy;

  const resolveConnId = useCallback(async () => {
    if (connectionId) return connectionId;
    if (!ensureConnection) return null;
    const row = await ensureConnection("telegram");
    return row?.id || null;
  }, [connectionId, ensureConnection]);

  const applyBotInfo = useCallback((r) => {
    if (r?.bot_name) setBotName(r.bot_name);
    if (r?.bot_username) setBotUsername(String(r.bot_username).replace(/^@/, ""));
  }, []);

  const buildConfigureBody = useCallback(() => {
    const wh = webhookUrl.trim();
    return {
      bot_token: normalizeToken(token),
      bot_name: botName.trim() || undefined,
      bot_username: botUsername.trim() ? `@${botUsername.trim().replace(/^@/, "")}` : undefined,
      webhook_url: isHttpsWebhook(wh) ? wh : undefined,
    };
  }, [botName, botUsername, token, webhookUrl]);

  const testConnection = useCallback(async () => {
    if (!canEdit) {
      toast.error("Only workspace admins can configure Telegram");
      return null;
    }
    if (!tokenValid) {
      toast.error("Enter a valid bot token from BotFather");
      return null;
    }
    setTestBusy(true);
    setError(null);
    setValidated(false);
    try {
      const connId = await resolveConnId();
      if (!connId) throw new Error("Couldn't prepare Telegram connection");
      const r = await channelsApi.testTelegram(connId, { bot_token: normalizeToken(token) });
      applyBotInfo(r);
      setValidated(true);
      toast.success("Token validated", {
        description: r.bot_username ? `${r.bot_username} responds on Telegram API` : "Bot token is valid",
      });
      return r;
    } catch (e) {
      const msg = e?.message || "Couldn't validate Telegram bot token";
      setError(msg);
      toast.error(msg);
      return null;
    } finally {
      setTestBusy(false);
    }
  }, [applyBotInfo, canEdit, resolveConnId, token, tokenValid]);

  const connectTelegram = useCallback(async () => {
    if (!canEdit) {
      toast.error("Only workspace admins can configure Telegram");
      return null;
    }
    if (!tokenValid) {
      toast.error("Enter a valid bot token from BotFather");
      return null;
    }
    setConnectBusy(true);
    setError(null);
    try {
      const connId = await resolveConnId();
      if (!connId) throw new Error("Couldn't prepare Telegram connection");
      const r = await channelsApi.configureTelegram(connId, buildConfigureBody());
      applyBotInfo(r);
      setValidated(true);
      if (r?.openclaw_synced === false) {
        const detail = r.openclaw_error || r.openclaw_warning
          || "Bot saved in Sellyx but OpenClaw sync failed.";
        setError(detail);
        toast.error("OpenClaw sync failed", { description: detail });
      } else {
        toast.success("Telegram Connected", {
          description: r.bot_username
            ? `${r.bot_username} is live on OpenClaw`
            : "Bot synced with OpenClaw",
        });
      }
      onConfigured?.(r);
      if (r?.webhook_warning) {
        toast.message("Telegram connected — webhook not set", { description: r.webhook_warning });
      }
      if (r?.ingest_hint) {
        toast.message("Message ingest ready", { description: r.ingest_hint });
      }
      return r;
    } catch (e) {
      const msg = e?.message || "Couldn't save Telegram configuration";
      setError(msg);
      toast.error(msg);
      return null;
    } finally {
      setConnectBusy(false);
    }
  }, [applyBotInfo, buildConfigureBody, canEdit, onConfigured, resolveConnId, tokenValid]);

  return {
    token, setToken,
    botName, setBotName,
    botUsername, setBotUsername,
    webhookUrl, setWebhookUrl,
    validated, error, testBusy, connectBusy, busy, tokenValid,
    testConnection, connectTelegram,
    resetValidation: () => { setValidated(false); setError(null); },
  };
}

export function TelegramConnectForm({
  tg,
  canEdit,
  showOptionalFields = true,
  testIdPrefix = "telegram",
}) {
  return (
    <div className="space-y-4">
      <div className="rounded-md bg-cyan-50 ring-1 ring-cyan-200 px-3 py-2 text-[11.5px] text-cyan-800 leading-snug">
        <div className="font-semibold mb-0.5">1. Open <span className="font-mono">@BotFather</span> in Telegram</div>
        <div>2. Send <span className="font-mono">/newbot</span> and copy the HTTP API token.</div>
        <div className="text-cyan-700/90">If BotFather says you can&apos;t create a bot, your Telegram account may be rate-limited — wait 24h or use an existing bot token from <span className="font-mono">/mybots</span>.</div>
        <div>3. Paste it below and click <span className="font-medium">Connect Telegram</span>.</div>
      </div>
      <div className="space-y-1.5">
        <label htmlFor={`${testIdPrefix}-token`} className="text-[11px] font-semibold text-slate-700">Telegram Bot Token</label>
        <Input
          id={`${testIdPrefix}-token`}
          value={tg.token}
          onChange={(e) => { tg.setToken(normalizeToken(e.target.value)); tg.resetValidation(); }}
          placeholder="123456789:AAxxxxxxxxxxxxxxxxxxxxxxxxxx"
          className="h-9 text-[12px] font-mono"
          disabled={!canEdit || tg.busy}
          data-testid={`${testIdPrefix}-token`}
        />
      </div>
      {showOptionalFields && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label htmlFor={`${testIdPrefix}-bot-name`} className="text-[11px] font-semibold text-slate-700">Bot Name <span className="text-slate-400 font-normal">(optional)</span></label>
              <Input
                id={`${testIdPrefix}-bot-name`}
                value={tg.botName}
                onChange={(e) => tg.setBotName(e.target.value)}
                placeholder="Auto-filled after connect"
                className="h-9 text-[12px]"
                disabled={!canEdit || tg.busy}
                data-testid={`${testIdPrefix}-bot-name`}
              />
            </div>
            <div className="space-y-1.5">
              <label htmlFor={`${testIdPrefix}-bot-username`} className="text-[11px] font-semibold text-slate-700">Bot Username <span className="text-slate-400 font-normal">(optional)</span></label>
              <Input
                id={`${testIdPrefix}-bot-username`}
                value={tg.botUsername}
                onChange={(e) => tg.setBotUsername(e.target.value.replace(/^@/, ""))}
                placeholder="my_bot"
                className="h-9 text-[12px] font-mono"
                disabled={!canEdit || tg.busy}
                data-testid={`${testIdPrefix}-bot-username`}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <label htmlFor={`${testIdPrefix}-webhook`} className="text-[11px] font-semibold text-slate-700">Webhook URL <span className="text-slate-400 font-normal">(leave empty — Bot API polling)</span></label>
            <Input
              id={`${testIdPrefix}-webhook`}
              value={tg.webhookUrl}
              onChange={(e) => tg.setWebhookUrl(e.target.value)}
              placeholder="Leave empty for OpenClaw Bot API polling"
              className="h-9 text-[12px] font-mono"
              disabled={!canEdit || tg.busy}
              data-testid={`${testIdPrefix}-webhook`}
            />
            <p className="text-[10.5px] text-slate-500 leading-snug">
              Sellix always syncs Telegram in polling mode. Entering a webhook URL is ignored —
              OpenClaw requires a webhook secret that Sellix does not manage.
            </p>
          </div>
        </>
      )}
      {tg.error && (
        <div className="rounded-md bg-rose-50 ring-1 ring-rose-200 px-3 py-2 text-[11px] text-rose-800">
          {tg.error}
        </div>
      )}
      {tg.validated && !tg.error && (
        <div className="rounded-md bg-emerald-50 ring-1 ring-emerald-200 px-3 py-2 text-[11px] text-emerald-800 inline-flex items-center gap-1.5">
          <ShieldCheck size={12} /> Token validated with Telegram API
        </div>
      )}
      <div className="flex flex-wrap justify-end gap-2 pt-1">
        <Button
          size="sm"
          variant="outline"
          className="h-8 text-[12px]"
          disabled={!canEdit || !tg.tokenValid || tg.busy}
          onClick={tg.testConnection}
          data-testid={`${testIdPrefix}-test`}
        >
          {tg.testBusy ? (
            <span className="inline-flex items-center gap-1.5">
              <Loader2 size={12} className="animate-spin" /> Testing…
            </span>
          ) : "Test Connection"}
        </Button>
        <Button
          size="sm"
          className="h-8 text-[12px]"
          disabled={!canEdit || !tg.tokenValid || tg.busy}
          onClick={tg.connectTelegram}
          data-testid={`${testIdPrefix}-connect`}
        >
          {tg.connectBusy ? (
            <span className="inline-flex items-center gap-1.5">
              <Loader2 size={12} className="animate-spin" /> Connecting…
            </span>
          ) : "Connect Telegram"}
        </Button>
      </div>
    </div>
  );
}

TelegramConnectForm.propTypes = {
  tg: telegramConfigurePropType.isRequired,
  canEdit: PropTypes.bool,
  showOptionalFields: PropTypes.bool,
  testIdPrefix: PropTypes.string,
};
